import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatCents, formatPercent } from '../lib/format';

interface CampaignSpendRow {
  campaign_id: number;
  external_id: string;
  name: string | null;
  source: string | null;
  status: string | null;
  spend_cents: number;
  impressions: number;
  clicks: number;
  new_patients: number;
  total_ltv_cents: number;
}

function isoDaysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function Campanhas() {
  const [rows, setRows] = useState<CampaignSpendRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<string>('');
  const [from, setFrom] = useState<string>(isoDaysAgo(30));
  const [to, setTo] = useState<string>(isoToday());

  useEffect(() => {
    setLoading(true);
    (async () => {
      const { data, error: e } = await supabase.rpc('campaign_spend_range', {
        p_from: from,
        p_to: to,
        p_source: source || null,
      });
      if (e) setError(e.message);
      else setError(null);
      setRows((data ?? []) as CampaignSpendRow[]);
      setLoading(false);
    })();
  }, [source, from, to]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.spend_cents ?? 0) - (a.spend_cents ?? 0)),
    [rows],
  );

  const totals = useMemo(() => {
    const spend = rows.reduce((s, r) => s + (r.spend_cents ?? 0), 0);
    const ltv = rows.reduce((s, r) => s + (r.total_ltv_cents ?? 0), 0);
    const pats = rows.reduce((s, r) => s + (r.new_patients ?? 0), 0);
    const clicks = rows.reduce((s, r) => s + (r.clicks ?? 0), 0);
    const imps = rows.reduce((s, r) => s + (r.impressions ?? 0), 0);
    const cpc = clicks > 0 ? spend / clicks : null;
    const roi = spend > 0 ? ((ltv - spend) / spend) * 100 : null;
    return { spend, ltv, pats, clicks, imps, cpc, roi };
  }, [rows]);

  function preset(days: number) {
    setFrom(isoDaysAgo(days));
    setTo(isoToday());
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold">Campanhas</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => preset(7)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">7d</button>
          <button onClick={() => preset(30)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">30d</button>
          <button onClick={() => preset(90)} className="px-2.5 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-100">90d</button>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
          />
          <span className="text-slate-400 text-sm">→</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
          />
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm"
          >
            <option value="">Todas</option>
            <option value="meta">Meta</option>
            <option value="google">Google</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm mb-4">{error}</div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Tile label="Investido" value={formatCents(totals.spend)} />
        <Tile label="Impressões" value={totals.imps.toLocaleString('pt-BR')} />
        <Tile label="Cliques" value={totals.clicks.toLocaleString('pt-BR')} />
        <Tile label="CPC médio" value={totals.cpc !== null ? formatCents(totals.cpc) : '—'} />
        <Tile label="Pacientes novos" value={String(totals.pats)} />
        <Tile label="ROI" value={formatPercent(totals.roi)} tone={totals.roi !== null && totals.roi > 0 ? 'good' : 'warn'} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3">Campanha</th>
              <th className="px-4 py-3">Fonte</th>
              <th className="px-4 py-3 text-right">Investido</th>
              <th className="px-4 py-3 text-right">Impressões</th>
              <th className="px-4 py-3 text-right">Cliques</th>
              <th className="px-4 py-3 text-right">CPC</th>
              <th className="px-4 py-3 text-right">Novos pac.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sem gasto no período.</td></tr>
            )}
            {sorted.map((r) => {
              const cpc = r.clicks > 0 ? r.spend_cents / r.clicks : null;
              return (
                <tr key={r.campaign_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{r.name ?? r.external_id}</td>
                  <td className="px-4 py-3 text-slate-600">{r.source ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{formatCents(r.spend_cents)}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{(r.impressions ?? 0).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{(r.clicks ?? 0).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{cpc !== null ? formatCents(cpc) : '—'}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{r.new_patients ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' | 'danger' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${
        tone === 'good' ? 'text-emerald-600' : tone === 'warn' ? 'text-amber-600' : tone === 'danger' ? 'text-red-600' : 'text-slate-900'
      }`}>{value}</div>
    </div>
  );
}
